import { useWindowWidth } from '@react-hook/window-size';
import cn from 'classnames';
import styles from './DestinationPage.module.scss';
import { SearchBox } from '../SearchBox/SearchBox';
import React, { useEffect, useState } from 'react';

import { InView } from 'react-intersection-observer';
import dynamic from 'next/dynamic';
import { ReviewItem } from '../Lists/ReviewItem';
import { httpGet } from '../../utils/httpClient';
import Head from 'next/head';
import { replaceHTML } from '../utils/replaceHTML';
import parse from 'html-react-parser';
import { BCBreadcrumbs, VintageButton } from '../../components/index';
import { WORLD_COUNTRIES } from '../../config/worldCountries';

const FeaturedContent = dynamic(() =>
  import('../FeaturedContent/FeaturedContent')
);
const RecommendationsList = dynamic(() =>
  import('../RecommendationsList/RecommendationsList')
);
const getHotel = async (country, city) => {
  const hotelRes = await httpGet(
    `/api/destinations/hotels?country=${country}&city=${city}&sort=random&size=1`
  );
  return await hotelRes.json();
};
export const DestinationPage = ({ data }) => {
  const [facts, setFacts] = useState();
  const [hotel, setHotel] = useState(null);
  const [seoSchema, setSeoSchema] = useState(null);

  useEffect( () => {
    if (!facts) {
      setFacts(JSON.parse(data.destination.destSection));
    }
    if (!seoSchema) {
      if (data.destination.contentMetaData?.seoSchema) {
        setSeoSchema(parse(data.destination?.contentMetaData.seoSchema));
      }
    }
  }, [data]);
  useEffect( () => {
    if (data && !hotel) {
      const fetchHotel = async () => {
        const hotel = await getHotel(
          data.destination.countrySafename,
          data.destination.citySafename
        );
        setHotel(hotel.content?.[0] || null);
      }
      fetchHotel().catch(console.error);
    }
  }, []);

  const width = useWindowWidth();

  function getArticleLink(type, subtype, textId, country, city) {
    if (!type || !subtype || !textId) {
      return null;
    }
    if (type === 'Hotels' && subtype === 'Reviews') {
      type = 'hotel';
      subtype = 'review';
    }

    if (country) {
      return `/destinations/${country}/${city ? city : textId}`;
    }

    return (
      '/' + type.toLowerCase() + '/' + subtype.toLowerCase() + '/' + textId
    );
  }

  function getFlag(countryCode) {
    const flag = WORLD_COUNTRIES.find((item) => item.code === countryCode).flags;
    if (flag) {
      return <img src={flag.svg || flag.png} alt={flag.alt}></img>
    }
  }

  return (
    <>
      <Head>
        <title>
          {data.destination.contentMetaData?.metaTitle ||
            `${
              data.destination.city === '-'
                ? data.destination.country
                : data.destination.city
            } - First-Class.com`}
        </title>
        {data.destination.contentMetaData?.seoHtml === 'true' && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <link
          rel="canonical"
          href={
            process.env.apiUrl + data.destination.contentMetaData?.canonicalTag
          }
        />
        <meta
          name="description"
          content={
            data.destination.contentMetaData?.metaDescription ||
            replaceHTML(data.destination.subHeading)
          }
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="first-class.com" />
        <meta
          name="twitter:title"
          content={
            data.destination.contentMetaData?.metaTitle ||
            `${
              data.destination.city === '-'
                ? data.destination.country
                : data.destination.city
            } - First-Class.com`
          }
        />
        <meta
          name="twitter:description"
          content={
            data.destination.contentMetaData?.metaDescription ||
            replaceHTML(data.destination.subHeading)
          }
        />
        <meta
          name="twitter:image"
          content={
            process.env.mediaUrl + data.destination.frontImage.replace('/', '')
          }
        />
        <meta
          property="og:title"
          content={
            data.destination.contentMetaData?.metaTitle ||
            `${
              data.destination.city === '-'
                ? data.destination.country
                : data.destination.city
            } - First-Class.com`
          }
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content={
            process.env.mediaUrl + data.destination.frontImage.replace('/', '')
          }
        />
        <meta
          property="og:description"
          content={
            data.destination.contentMetaData?.metaDescription ||
            replaceHTML(data.destination.subHeading)
          }
        />
        {seoSchema}
      </Head>
      <div className={'bg-review-bg'}>
        <div className={cn(styles.topSection)}>
          {data.destination.frontVideo && width >= 1024 && (
            <video
              src={
                process.env.mediaUrl +
                data.destination.frontVideo.replace('/', '')
              }
              autoPlay
              muted
              loop
            />
          )}
          {((data.destination.frontImage && !data.destination.frontVideo) ||
            (data.destination.frontImage && width < 1024)) && (
            <img
              src={
                process.env.mediaUrl +
                data.destination.frontImage.replace('/', '')
              }
              alt={`Destination - ${data.destination.heading}`}
            />
          )}

          <div className={cn(styles.topSectionText, 'font-fragment')}>
            <div className={styles.heading}>{data.destination.heading}</div>
            <div className={styles.description}>
              {data.destination.subHeading}
            </div>
          </div>

        </div>
        <div className={cn(styles.content)}>
          <div
            className={cn(styles.topSectionSearch, 'container mx-auto px-4')}
          >
            <SearchBox type={'mainSearch'} activeTab={0} searchSource={'Destination'} />
          </div>
          <div className="container">
            <div className={cn(styles.contentSearch, 'mx-auto px-4')}>
              <SearchBox type={'sideSearch'} activeTab={0} searchSource={'Destination'} />
            </div>
            <div className={cn(styles.introduction)}>
              <BCBreadcrumbs headerText={data.destination.heading} />
              <h1 className={cn(styles.introductionHeading, 'font-fragment')}>
                {data.destination.introTitle}
              </h1>
              <div
                className={cn(styles.introductionDescription, 'font-libre')}
                dangerouslySetInnerHTML={{ __html: data.destination.intro }}
              />
            </div>
          </div>
          <div className={styles.carouselBack}>
            <div className="container">
              <InView triggerOnce>
                {({ inView, ref }) => (
                  <div
                    ref={ref}
                    className={cn(
                      styles.carouselWrapper,
                      'w-full sm:px-4 mx-auto'
                    )}
                  >
                    {inView && (
                      <FeaturedContent
                        hideHeading
                        items={data.destination.topLists}
                        enableInfinite={true}
                        executePaginate={false}
                        showThumbs={true}
                      />
                    )}
                  </div>
                )}
              </InView>
            </div>
          </div>
          <div className="container">
            <div className={cn(styles.introduction)}>
              <div className={cn(styles.introductionHeading, 'font-fragment')}>
                {data.destination.flightSectionTitle}
              </div>
              <div
                className={cn(styles.introductionDescription, 'font-libre')}
                dangerouslySetInnerHTML={{
                  __html: data.destination.flightSection
                }}
              />
            </div>
            <div className={cn(styles.contentBoxes)}>
              <div className={cn(styles.destination)}>
                {!!data.destination.guides.length && (
                  <div className={cn(styles.destinationRecommendation)}>
                    <RecommendationsList
                      items={data.destination.guides}
                      hideHeading
                    />
                  </div>
                )}
                <div className={cn(styles.destinationFacts)}>
                  <div className={cn(styles.factsHeading, 'font-fragment')}>
                    <div>
                      {data.destination.city === '-'
                        ? data.destination.country
                        : data.destination.city}{' '}
                      Facts
                    </div>
                    <div className={cn(styles.factsFlag)}>
                      {/* <Flag
                        code={
                          data.destination.countyCode ||
                          data.destination.countryCode
                        }
                      /> */}

                      {getFlag(data.destination.countryCode)}
                    </div>
                  </div>
                  {!!facts?.length &&
                    facts.map((fact) => (
                      <div
                        key={fact.name}
                        className={cn(styles.fact, 'font-libre')}
                      >
                        <div className={styles.factName}>{fact.name}:</div>
                        <div className={styles.factDescription}>{fact.value}</div>
                      </div>
                    ))}
                </div>

                {!!data.destination.faqs.length && (
                  <div className="flex justify-center my-10">
                    <VintageButton
                      href={getArticleLink(
                        data.destination.faqs[0].type,
                        data.destination.faqs[0].subtype,
                        data.destination.faqs[0].textId,
                        data.destination.faqs[0].country,
                        data.destination.faqs[0].city
                      )}
                      label={`${data.city === '-' ? data.country : data.city} Faq`}
                      target="_blank"
                      dark={true}
                    >
                    </VintageButton>
                  </div>
                )}
              </div>
              <div>
                <div className={cn(styles.hotel)}>
                  {!!hotel && (
                    <ReviewItem
                      type="Hotel"
                      starRating={true}
                      review={hotel}
                      url={`/hotel/review/${hotel.textId}`}
                    />
                  )}
                  {!hotel && <div className="mx-auto mt-8">Please add hotel</div>}
                </div>
                <div className="flex justify-center mb-2 mt-10">
                  {!!hotel && (
                    <VintageButton
                      href={`/destinations/${data.destination.countrySafename}/${data.destination.citySafename}/hotels`}
                      className={cn(styles.moreHotelsButton, 'font-libre mx-auto')}
                      dark={true}
                      label={`More hotels in 
                      ${data.destination.citySafename !== '-'
                        ? data.destination.city
                        : data.destination.country}`}
                    >
                    </VintageButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
